import {
  GeoApiError,
  GeoError,
  GeoRateLimitError,
  GeocodingError,
  GoogleClient,
  GoogleGeocodingProvider,
  GoogleRoutesProvider,
  GoogleSuggestionProvider,
  GoogleTimezoneProvider,
  LocalTimezoneProvider,
  RoutingError,
  SuggestionError,
  createDefaultProviders,
  createFetchExecutor,
  createGoogleGeocoder,
  createGoogleProviders,
  createGoogleRouter,
  createGoogleSuggester,
  getDefaultGeoProviders,
  getGoogleApiKey,
  getLogger,
  init,
  isInitialized,
  resetDefaultExecutor,
  resetLanesConfig,
  resolveExecutor,
  resolveTimezone
} from "./chunk-X4N6ZUIX.mjs";

// src/errors/location-errors.ts
var LocationError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "LocationError";
  }
};
var LocationNotFoundError = class extends LocationError {
  constructor(criteria) {
    super(`Location not found: ${JSON.stringify(criteria)}`);
    this.criteria = criteria;
    this.name = "LocationNotFoundError";
  }
};
var LocationValidationError = class extends LocationError {
  constructor(zodError) {
    super(`Location validation failed: ${zodError.message}`);
    this.zodError = zodError;
    this.name = "LocationValidationError";
  }
};
var GeocodingError2 = class extends LocationError {
  constructor(address, reason) {
    super(`Geocoding failed for "${address}": ${reason}`);
    this.address = address;
    this.reason = reason;
    this.name = "GeocodingError";
  }
};
var AmbiguousLocationError = class extends LocationError {
  constructor(candidates) {
    super(`Multiple candidate locations found. User selection required.`);
    this.candidates = candidates;
    this.name = "AmbiguousLocationError";
  }
};

// src/errors/lane-errors.ts
var LaneError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "LaneError";
  }
};
var LaneNotFoundError = class extends LaneError {
  constructor(criteria) {
    super(`Lane not found: ${JSON.stringify(criteria)}`);
    this.criteria = criteria;
    this.name = "LaneNotFoundError";
  }
};
var LaneValidationError = class extends LaneError {
  constructor(zodError) {
    super(`Lane validation failed: ${zodError.message}`);
    this.zodError = zodError;
    this.name = "LaneValidationError";
  }
};
var DuplicateLocationError = class extends LaneError {
  constructor(locationId) {
    super(`Origin and destination cannot be the same location (id=${locationId})`);
    this.locationId = locationId;
    this.name = "DuplicateLocationError";
  }
};
var LaneNotReadyError = class extends LaneError {
  constructor(laneId, status) {
    super(`Lane ${laneId} is not ready (status=${status})`);
    this.laneId = laneId;
    this.status = status;
    this.name = "LaneNotReadyError";
  }
};
var LaneEnrichmentFailedError = class extends LaneError {
  constructor(laneId, cause) {
    super(`Lane ${laneId} enrichment failed${cause ? `: ${cause.message}` : ""}`);
    this.laneId = laneId;
    this.cause = cause;
    this.name = "LaneEnrichmentFailedError";
  }
};

// src/features/resolve-location/resolveLocation.ts
import { resolveExecutor as resolveExecutor4, getLogger as getLogger6 } from "@hopdrive/sdk-core";

// ../../gql/operations.ts
import { resolveExecutor as resolveExecutor2 } from "@hopdrive/sdk-core";

// ../../gql/schema.ts
var UpdateLaneEnrichmentDocument = { "__text": 'mutation UpdateLaneEnrichment($id: bigint!, $description: String, $distanceMiles: numeric, $durationSec: bigint, $averageDriveSpeedMph: numeric, $averageDriveSpeedMinPerMile: numeric, $pickupInspectionSec: bigint, $deliveryInspectionSec: bigint, $returnRideWaitSec: bigint, $tolls: numeric, $routePolyline: String, $routeMetadata: jsonb, $enrichmentSource: String, $creationCriteria: jsonb, $status: String!, $enrichedAt: timestamptz) {\n  update_lanes_by_pk(\n    pk_columns: {id: $id}\n    _set: {description: $description, distance_miles: $distanceMiles, duration_sec: $durationSec, average_drive_speed_mph: $averageDriveSpeedMph, average_drive_speed_min_per_mile: $averageDriveSpeedMinPerMile, pickup_inspection_sec: $pickupInspectionSec, delivery_inspection_sec: $deliveryInspectionSec, return_ride_wait_sec: $returnRideWaitSec, tolls: $tolls, route_polyline: $routePolyline, route_metadata: $routeMetadata, enrichment_source: $enrichmentSource, creation_criteria: $creationCriteria, status: $status, enriched_at: $enrichedAt, updatedat: "now()"}\n  ) {\n    id\n    status\n    distance_miles\n    duration_sec\n    tolls\n    route_polyline\n    enriched_at\n    updatedat\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLaneEnrichment" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "description" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "distanceMiles" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "durationSec" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "averageDriveSpeedMph" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "averageDriveSpeedMinPerMile" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "pickupInspectionSec" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "deliveryInspectionSec" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "returnRideWaitSec" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "tolls" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "routePolyline" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "routeMetadata" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "jsonb" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "enrichmentSource" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "creationCriteria" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "jsonb" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "enrichedAt" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_lanes_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "pk_columns" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "description" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "description" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "distance_miles" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "distanceMiles" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "duration_sec" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "durationSec" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "average_drive_speed_mph" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "averageDriveSpeedMph" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "average_drive_speed_min_per_mile" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "averageDriveSpeedMinPerMile" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_inspection_sec" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "pickupInspectionSec" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "delivery_inspection_sec" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "deliveryInspectionSec" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "return_ride_wait_sec" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "returnRideWaitSec" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "tolls" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "tolls" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "route_polyline" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "routePolyline" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "route_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "routeMetadata" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "enrichment_source" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "enrichmentSource" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "creation_criteria" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "creationCriteria" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "enriched_at" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "enrichedAt" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedat" }, "value": { "kind": "StringValue", "value": "now()", "block": false } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tolls" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enriched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] };
var FindGeocodeCacheDocument = { "__text": 'query FindGeocodeCache($normalizedAddress: String!) {\n  geocode_cache(\n    where: {normalized_address: {_eq: $normalizedAddress}, expires_at: {_gt: "now()"}}\n    limit: 1\n  ) {\n    geocode_result\n    expires_at\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "FindGeocodeCache" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "normalizedAddress" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "geocode_cache" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "normalized_address" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "normalizedAddress" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "expires_at" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gt" }, "value": { "kind": "StringValue", "value": "now()", "block": false } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "geocode_result" } }, { "kind": "Field", "name": { "kind": "Name", "value": "expires_at" } }] } }] } }] };
var StoreGeocodeCacheDocument = { "__text": "mutation StoreGeocodeCache($normalizedAddress: String!, $geocodeResult: jsonb!, $expiresAt: timestamptz!) {\n  insert_geocode_cache_one(\n    object: {normalized_address: $normalizedAddress, geocode_result: $geocodeResult, expires_at: $expiresAt}\n    on_conflict: {constraint: uq_geocode_cache_address, update_columns: [geocode_result, expires_at]}\n  ) {\n    id\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "StoreGeocodeCache" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "normalizedAddress" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "geocodeResult" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "jsonb" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "expiresAt" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "insert_geocode_cache_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "normalized_address" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "normalizedAddress" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "geocode_result" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "geocodeResult" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "expires_at" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "expiresAt" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "on_conflict" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "constraint" }, "value": { "kind": "EnumValue", "value": "uq_geocode_cache_address" } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "update_columns" }, "value": { "kind": "ListValue", "values": [{ "kind": "EnumValue", "value": "geocode_result" }, { "kind": "EnumValue", "value": "expires_at" }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] };
var GetLaneByIdDocument = { "__text": "query GetLaneById($id: bigint!) {\n  lanes_by_pk(id: $id) {\n    id\n    active\n    status\n    updatedat\n    customer_id\n    origin_location_id\n    destination_location_id\n    description\n    distance_miles\n    duration_sec\n    tolls\n    favorite\n    route_polyline\n    route_metadata\n    enriched_at\n    enrichment_source\n    creation_criteria\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetLaneById" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "lanes_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "origin_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "destination_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tolls" } }, { "kind": "Field", "name": { "kind": "Name", "value": "favorite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_metadata" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enriched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enrichment_source" } }, { "kind": "Field", "name": { "kind": "Name", "value": "creation_criteria" } }] } }] } }] };
var GetLaneWithInverseDocument = { "__text": "query GetLaneWithInverse($id: bigint!) {\n  lanes_by_pk(id: $id) {\n    id\n    active\n    status\n    updatedat\n    distance_miles\n    duration_sec\n    route_polyline\n    route_metadata\n    enriched_at\n    enrichment_source\n    creation_criteria\n    inverse {\n      id\n      active\n      status\n      updatedat\n      distance_miles\n      duration_sec\n      route_polyline\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetLaneWithInverse" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "lanes_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_metadata" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enriched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enrichment_source" } }, { "kind": "Field", "name": { "kind": "Name", "value": "creation_criteria" } }, { "kind": "Field", "name": { "kind": "Name", "value": "inverse" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }] } }] } }] } }] };
var GetLaneWithLocationsDocument = { "__text": "query GetLaneWithLocations($id: bigint!) {\n  lanes_by_pk(id: $id) {\n    id\n    active\n    status\n    updatedat\n    customer_id\n    description\n    distance_miles\n    duration_sec\n    pickup_inspection_sec\n    delivery_inspection_sec\n    return_ride_wait_sec\n    tolls\n    route_polyline\n    route_metadata\n    enriched_at\n    enrichment_source\n    creation_criteria\n    pickup {\n      id\n      name\n      address\n      latitude\n      longitude\n      region_id\n    }\n    delivery {\n      id\n      name\n      address\n      latitude\n      longitude\n      region_id\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetLaneWithLocations" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "lanes_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery_inspection_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "return_ride_wait_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tolls" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_metadata" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enriched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enrichment_source" } }, { "kind": "Field", "name": { "kind": "Name", "value": "creation_criteria" } }, { "kind": "Field", "name": { "kind": "Name", "value": "pickup" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "delivery" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }] } }] } }] } }] };
var UpdateLaneDescriptionDocument = { "__text": "mutation UpdateLaneDescription($id: bigint!, $description: String!) {\n  update_lanes_by_pk(pk_columns: {id: $id}, _set: {description: $description}) {\n    id\n    description\n    updatedat\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLaneDescription" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "description" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_lanes_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "pk_columns" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "description" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "description" } } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] };
var UpdateLaneStatusDocument = { "__text": 'mutation UpdateLaneStatus($ids: [bigint!]!, $status: String!) {\n  update_lanes(\n    where: {id: {_in: $ids}}\n    _set: {status: $status, updatedat: "now()"}\n  ) {\n    affected_rows\n    returning {\n      id\n      status\n      updatedat\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLaneStatus" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "ids" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_lanes" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "ids" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedat" }, "value": { "kind": "StringValue", "value": "now()", "block": false } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "affected_rows" } }, { "kind": "Field", "name": { "kind": "Name", "value": "returning" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] } }] };
var UpdateLaneStatusGuardedDocument = { "__text": "mutation UpdateLaneStatusGuarded($where: lanes_bool_exp!, $set: lanes_set_input!) {\n  update_lanes(where: $where, _set: $set) {\n    affected_rows\n    returning {\n      id\n      status\n      enriched_at\n      updatedat\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLaneStatusGuarded" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "lanes_bool_exp" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "set" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "lanes_set_input" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_lanes" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "where" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "set" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "affected_rows" } }, { "kind": "Field", "name": { "kind": "Name", "value": "returning" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enriched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] } }] };
var UpdateLaneStatusReadyDocument = { "__text": 'mutation UpdateLaneStatusReady($ids: [bigint!]!, $enrichedAt: timestamptz!) {\n  update_lanes(\n    where: {id: {_in: $ids}}\n    _set: {status: "ready", enriched_at: $enrichedAt, updatedat: "now()"}\n  ) {\n    affected_rows\n    returning {\n      id\n      status\n      enriched_at\n      updatedat\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLaneStatusReady" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "ids" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "enrichedAt" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_lanes" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "ids" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "StringValue", "value": "ready", "block": false } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "enriched_at" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "enrichedAt" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedat" }, "value": { "kind": "StringValue", "value": "now()", "block": false } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "affected_rows" } }, { "kind": "Field", "name": { "kind": "Name", "value": "returning" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "enriched_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] } }] };
var FindLocationByInputAliasDocument = { "__text": "query FindLocationByInputAlias($customerId: bigint!, $inputAlias: String!) {\n  location_input_aliases(\n    where: {customer_id: {_eq: $customerId}, input_alias: {_eq: $inputAlias}}\n    limit: 1\n  ) {\n    location {\n      id\n      customer_id\n      name\n      address\n      address_line_one\n      address_line_two\n      latitude\n      longitude\n      place_id\n      region_id\n      region {\n        id\n        name\n        status\n      }\n      timezone\n      type\n      active\n      customer_ref\n      plus_code\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "FindLocationByInputAlias" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "inputAlias" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "location_input_aliases" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "input_alias" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "inputAlias" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "location" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_one" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_two" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_ref" } }, { "kind": "Field", "name": { "kind": "Name", "value": "plus_code" } }] } }] } }] } }] };
var FindLocationsByPlaceIdDocument = { "__text": "query FindLocationsByPlaceId($customerId: bigint!, $placeId: String!) {\n  locations(\n    where: {customer_id: {_eq: $customerId}, place_id: {_eq: $placeId}, place_id_authoritative: {_eq: true}, active: {_eq: 1}}\n    limit: 1\n  ) {\n    id\n    customer_id\n    name\n    address\n    address_line_one\n    address_line_two\n    latitude\n    longitude\n    place_id\n    region_id\n    region {\n      id\n      name\n      status\n    }\n    timezone\n    type\n    active\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "FindLocationsByPlaceId" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "placeId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "locations" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "place_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "placeId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "place_id_authoritative" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "BooleanValue", "value": true } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_one" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_two" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }] } }] } }] };
var FindLocationsNearbyDocument = { "__text": "query FindLocationsNearby($customerId: bigint!, $point: geography!, $radiusMeters: Float!) {\n  locations(\n    where: {customer_id: {_eq: $customerId}, active: {_eq: 1}, geopoint: {_st_d_within: {distance: $radiusMeters, from: $point}}}\n    limit: 11\n  ) {\n    id\n    customer_id\n    name\n    nickname\n    address\n    address_line_one\n    address_line_two\n    city\n    state\n    zip_code\n    latitude\n    longitude\n    place_id\n    region_id\n    region {\n      id\n      name\n      status\n    }\n    timezone\n    type\n    favorite\n    active\n    createdat\n    updatedat\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "FindLocationsNearby" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "point" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "geography" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "radiusMeters" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Float" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "locations" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "geopoint" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_st_d_within" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "distance" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "radiusMeters" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "from" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "point" } } }] } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "11" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "nickname" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_one" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_two" } }, { "kind": "Field", "name": { "kind": "Name", "value": "city" } }, { "kind": "Field", "name": { "kind": "Name", "value": "state" } }, { "kind": "Field", "name": { "kind": "Name", "value": "zip_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "favorite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] };
var GetLocationByCustomerRefDocument = { "__text": "query GetLocationByCustomerRef($customerId: bigint!, $customerRef: String!) {\n  locations(\n    where: {customer_id: {_eq: $customerId}, customer_ref: {_eq: $customerRef}, active: {_eq: 1}}\n    limit: 1\n  ) {\n    id\n    customer_id\n    name\n    address\n    address_line_one\n    address_line_two\n    latitude\n    longitude\n    place_id\n    region_id\n    region {\n      id\n      name\n      status\n    }\n    timezone\n    type\n    active\n    customer_ref\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetLocationByCustomerRef" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerRef" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "locations" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_ref" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerRef" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_one" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_two" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_ref" } }] } }] } }] };
var GetLocationByIdDocument = { "__text": "query GetLocationById($id: bigint!) {\n  locations_by_pk(id: $id) {\n    id\n    customer_id\n    name\n    nickname\n    address\n    address_line_one\n    address_line_two\n    city\n    state\n    zip_code\n    latitude\n    longitude\n    place_id\n    region_id\n    region {\n      id\n      name\n      status\n    }\n    timezone\n    type\n    active\n    customer_ref\n    plus_code\n    coordinates_source\n    place_id_authoritative\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetLocationById" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "locations_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "nickname" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_one" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_two" } }, { "kind": "Field", "name": { "kind": "Name", "value": "city" } }, { "kind": "Field", "name": { "kind": "Name", "value": "state" } }, { "kind": "Field", "name": { "kind": "Name", "value": "zip_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "type" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_ref" } }, { "kind": "Field", "name": { "kind": "Name", "value": "plus_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "coordinates_source" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id_authoritative" } }] } }] } }] };
var UpdateLocationCoordinatesDocument = { "__text": "mutation UpdateLocationCoordinates($id: bigint!, $latitude: numeric!, $longitude: numeric!, $coordinates_source: String) {\n  update_locations_by_pk(\n    pk_columns: {id: $id}\n    _set: {latitude: $latitude, longitude: $longitude, coordinates_source: $coordinates_source}\n  ) {\n    id\n    latitude\n    longitude\n    coordinates_source\n    updatedat\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLocationCoordinates" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "latitude" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "longitude" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "coordinates_source" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_locations_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "pk_columns" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "latitude" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "latitude" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "longitude" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "longitude" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "coordinates_source" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "coordinates_source" } } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "coordinates_source" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] };
var UpdateLocationTimezoneDocument = { "__text": "mutation UpdateLocationTimezone($id: bigint!, $timezone: String!) {\n  update_locations_by_pk(pk_columns: {id: $id}, _set: {timezone: $timezone}) {\n    id\n    timezone\n    updatedat\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "UpdateLocationTimezone" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "timezone" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_locations_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "pk_columns" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "timezone" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "timezone" } } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }] } }] } }] };
var CreateLaneShellPairDocument = { "__text": "mutation CreateLaneShellPair($forwardLane: lanes_insert_input!, $inverseLane: lanes_insert_input!) {\n  forward: insert_lanes_one(\n    object: $forwardLane\n    on_conflict: {constraint: lanes_customer_id_origin_location_id_destination_location_id_ke, update_columns: [updatedat]}\n  ) {\n    id\n    customer_id\n    active\n    status\n    origin_location_id\n    destination_location_id\n  }\n  inverse: insert_lanes_one(\n    object: $inverseLane\n    on_conflict: {constraint: lanes_customer_id_origin_location_id_destination_location_id_ke, update_columns: [updatedat]}\n  ) {\n    id\n    customer_id\n    active\n    status\n    origin_location_id\n    destination_location_id\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "CreateLaneShellPair" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "forwardLane" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "lanes_insert_input" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "inverseLane" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "lanes_insert_input" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "alias": { "kind": "Name", "value": "forward" }, "name": { "kind": "Name", "value": "insert_lanes_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "forwardLane" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "on_conflict" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "constraint" }, "value": { "kind": "EnumValue", "value": "lanes_customer_id_origin_location_id_destination_location_id_ke" } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "update_columns" }, "value": { "kind": "ListValue", "values": [{ "kind": "EnumValue", "value": "updatedat" }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "origin_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "destination_location_id" } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "inverse" }, "name": { "kind": "Name", "value": "insert_lanes_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "inverseLane" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "on_conflict" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "constraint" }, "value": { "kind": "EnumValue", "value": "lanes_customer_id_origin_location_id_destination_location_id_ke" } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "update_columns" }, "value": { "kind": "ListValue", "values": [{ "kind": "EnumValue", "value": "updatedat" }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "origin_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "destination_location_id" } }] } }] } }] };
var ResolveLaneByLocationsDocument = { "__text": "query ResolveLaneByLocations($customerId: bigint!, $originLocationId: bigint!, $destinationLocationId: bigint!) {\n  lanes(\n    where: {customer_id: {_eq: $customerId}, origin_location_id: {_eq: $originLocationId}, destination_location_id: {_eq: $destinationLocationId}}\n    limit: 1\n  ) {\n    id\n    active\n    status\n    customer_id\n    origin_location_id\n    destination_location_id\n    description\n    distance_miles\n    duration_sec\n    tolls\n    favorite\n    createdat\n    updatedat\n    route_polyline\n    inverse {\n      id\n      active\n      status\n      customer_id\n      origin_location_id\n      destination_location_id\n      description\n      distance_miles\n      duration_sec\n      tolls\n      favorite\n      createdat\n      updatedat\n      route_polyline\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "ResolveLaneByLocations" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "originLocationId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "destinationLocationId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "lanes" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "origin_location_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "originLocationId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "destination_location_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "destinationLocationId" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "origin_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "destination_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tolls" } }, { "kind": "Field", "name": { "kind": "Name", "value": "favorite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }, { "kind": "Field", "name": { "kind": "Name", "value": "inverse" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "origin_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "destination_location_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "distance_miles" } }, { "kind": "Field", "name": { "kind": "Name", "value": "duration_sec" } }, { "kind": "Field", "name": { "kind": "Name", "value": "tolls" } }, { "kind": "Field", "name": { "kind": "Name", "value": "favorite" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "updatedat" } }, { "kind": "Field", "name": { "kind": "Name", "value": "route_polyline" } }] } }] } }] } }] };
var TriggerLaneEnrichmentDocument = { "__text": 'mutation TriggerLaneEnrichment($ids: [bigint!]!) {\n  update_lanes(\n    where: {id: {_in: $ids}, status: {_eq: "new"}}\n    _set: {status: "pending", updatedat: "now()"}\n  ) {\n    affected_rows\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "TriggerLaneEnrichment" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "ids" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_lanes" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "ids" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "new", "block": false } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "StringValue", "value": "pending", "block": false } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "updatedat" }, "value": { "kind": "StringValue", "value": "now()", "block": false } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "affected_rows" } }] } }] } }] };
var CreateLocationDocument = { "__text": "mutation CreateLocation($location: locations_insert_input!) {\n  insert_locations_one(\n    object: $location\n    on_conflict: {constraint: locations_customer_id_address_key, update_columns: [updatedat, latitude, longitude, place_id, active]}\n  ) {\n    id\n    customer_id\n    name\n    address\n    address_line_one\n    address_line_two\n    city\n    state\n    zip_code\n    latitude\n    longitude\n    place_id\n    region_id\n    region {\n      id\n      name\n      status\n    }\n    timezone\n    active\n    createdat\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "CreateLocation" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "location" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "locations_insert_input" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "insert_locations_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "location" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "on_conflict" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "constraint" }, "value": { "kind": "EnumValue", "value": "locations_customer_id_address_key" } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "update_columns" }, "value": { "kind": "ListValue", "values": [{ "kind": "EnumValue", "value": "updatedat" }, { "kind": "EnumValue", "value": "latitude" }, { "kind": "EnumValue", "value": "longitude" }, { "kind": "EnumValue", "value": "place_id" }, { "kind": "EnumValue", "value": "active" }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_one" } }, { "kind": "Field", "name": { "kind": "Name", "value": "address_line_two" } }, { "kind": "Field", "name": { "kind": "Name", "value": "city" } }, { "kind": "Field", "name": { "kind": "Name", "value": "state" } }, { "kind": "Field", "name": { "kind": "Name", "value": "zip_code" } }, { "kind": "Field", "name": { "kind": "Name", "value": "latitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "longitude" } }, { "kind": "Field", "name": { "kind": "Name", "value": "place_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "region" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }, { "kind": "Field", "name": { "kind": "Name", "value": "active" } }, { "kind": "Field", "name": { "kind": "Name", "value": "createdat" } }] } }] } }] };
var GetRegionByCoordinatesDocument = { "__text": "query GetRegionByCoordinates($point: geometry!) {\n  regions(where: {geofence: {_st_contains: $point}}) {\n    id\n    name\n    status\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetRegionByCoordinates" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "point" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "geometry" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "regions" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "geofence" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_st_contains" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "point" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }] } }] };
var RecordInputAliasDocument = { "__text": "mutation RecordInputAlias($locationId: bigint!, $customerId: bigint!, $inputAlias: String!) {\n  insert_location_input_aliases_one(\n    object: {location_id: $locationId, customer_id: $customerId, input_alias: $inputAlias}\n    on_conflict: {constraint: uq_location_alias, update_columns: []}\n  ) {\n    id\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "RecordInputAlias" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "locationId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "inputAlias" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "insert_location_input_aliases_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "location_id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "locationId" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "customer_id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "input_alias" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "inputAlias" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "on_conflict" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "constraint" }, "value": { "kind": "EnumValue", "value": "uq_location_alias" } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "update_columns" }, "value": { "kind": "ListValue", "values": [] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] };

// ../../gql/operations.ts
async function createLaneShellPair(variables, options) {
  const { executor, ...requestOptions } = options ?? {};
  return resolveExecutor2(executor).mutate(
    CreateLaneShellPairDocument,
    variables,
    requestOptions
  );
}
async function createLocation(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    CreateLocationDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.insert_locations_one;
}
async function findGeocodeCache(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    FindGeocodeCacheDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.geocode_cache;
}
async function findLocationByInputAlias(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    FindLocationByInputAliasDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.location_input_aliases;
}
async function findLocationsByPlaceId(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    FindLocationsByPlaceIdDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.locations;
}
async function findLocationsNearby(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    FindLocationsNearbyDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.locations;
}
async function getLaneById(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetLaneByIdDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.lanes_by_pk;
}
async function getLaneWithInverse(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetLaneWithInverseDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.lanes_by_pk;
}
async function getLaneWithLocations(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetLaneWithLocationsDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.lanes_by_pk;
}
async function getLocationByCustomerRef(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetLocationByCustomerRefDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.locations;
}
async function getLocationById(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetLocationByIdDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.locations_by_pk;
}
async function getRegionByCoordinates(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetRegionByCoordinatesDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.regions;
}
async function recordInputAlias(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    RecordInputAliasDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.insert_location_input_aliases_one;
}
async function resolveLaneByLocations(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    ResolveLaneByLocationsDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.lanes;
}
async function storeGeocodeCache(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    StoreGeocodeCacheDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.insert_geocode_cache_one;
}
async function triggerLaneEnrichment(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    TriggerLaneEnrichmentDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_lanes;
}
async function updateLaneDescription(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLaneDescriptionDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_lanes_by_pk;
}
async function updateLaneEnrichment(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLaneEnrichmentDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_lanes_by_pk;
}
async function updateLaneStatus(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLaneStatusDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_lanes;
}
async function updateLaneStatusGuarded(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLaneStatusGuardedDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_lanes;
}
async function updateLaneStatusReady(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLaneStatusReadyDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_lanes;
}
async function updateLocationCoordinates(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLocationCoordinatesDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_locations_by_pk;
}
async function updateLocationTimezone(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).mutate(
    UpdateLocationTimezoneDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_locations_by_pk;
}

// src/features/location-lookup/getLocation.ts
async function getLocation(params) {
  const location = await getLocationById(
    { id: params.id },
    { executor: params.executor }
  );
  if (!location && params.required) {
    throw new LocationNotFoundError({ id: params.id });
  }
  return location ?? null;
}

// src/utils/normalize.ts
function normalizeInputForAlias(input) {
  return input.toLowerCase().trim().replace(/\s+/g, " ");
}
function normalizeForComparison(input) {
  let normalized = input.toLowerCase().trim().replace(/[.,-]/g, "").replace(/\s+/g, " ");
  const streetAbbreviations = {
    "st": "street",
    "ave": "avenue",
    "blvd": "boulevard",
    "dr": "drive",
    "ln": "lane",
    "rd": "road",
    "ct": "court",
    "cir": "circle",
    "pl": "place",
    "ter": "terrace",
    "hwy": "highway",
    "pkwy": "parkway",
    // More USPS street suffixes (Publication 28). Without 'fwy', "8741 N Fwy"
    // and Google's "8741 North Freeway" were different streets, and a Hertz
    // order refused an address its customer already had.
    "fwy": "freeway",
    "frwy": "freeway",
    "expy": "expressway",
    "expwy": "expressway",
    "tpke": "turnpike",
    "trl": "trail",
    "xing": "crossing",
    "plz": "plaza",
    "sq": "square",
    "ctr": "center",
    "rte": "route",
    "aly": "alley",
    "byp": "bypass",
    "cswy": "causeway",
    "ste": "suite",
    "apt": "apartment",
    "bldg": "building"
  };
  const directionals = {
    "n": "north",
    "s": "south",
    "e": "east",
    "w": "west",
    "ne": "northeast",
    "nw": "northwest",
    "se": "southeast",
    "sw": "southwest"
  };
  for (const [abbr, full] of Object.entries(streetAbbreviations)) {
    normalized = normalized.replace(new RegExp(`\\b${abbr}\\b`, "g"), full);
  }
  for (const [abbr, full] of Object.entries(directionals)) {
    normalized = normalized.replace(new RegExp(`\\b${abbr}\\b`, "g"), full);
  }
  return normalized;
}
function normalizeNameForComparison(input) {
  let normalized = normalizeForComparison(input);
  const businessSuffixes = ["llc", "inc", "corp", "ltd", "co", "company", "corporation"];
  for (const suffix of businessSuffixes) {
    normalized = normalized.replace(new RegExp(`\\b${suffix}\\b`, "g"), "");
  }
  return normalized.trim().replace(/\s+/g, " ");
}
function normalizeSuite(addressLineTwo) {
  const unit = unitFromAddressLineTwo(addressLineTwo);
  if (!unit) return null;
  return unit.toLowerCase().replace(unitPrefixPattern(), "").replace(/[^a-z0-9]/g, "").trim() || null;
}
var SUITE_PATTERN = /(?:\b(?:suite|ste|unit|apt|apartment|building|bldg|room|rm|floor|door)(?=[\s.#\d])\.?|\bfl(?=[\s.#\d])\.?(?!\s*\d{5}(?:-\d{4})?\b)|#)\s*[a-z0-9][\w-]*/i;
function extractSuiteFromAddress(address) {
  if (!address) return null;
  const match = address.match(SUITE_PATTERN);
  return match ? match[0] : null;
}
function getEffectiveSuite(location) {
  const fromLineTwo = normalizeSuite(location.address_line_two);
  if (fromLineTwo) return fromLineTwo;
  const extractedFromLineOne = extractSuiteFromAddress(location.address_line_one);
  if (extractedFromLineOne) return normalizeSuite(extractedFromLineOne);
  const extractedFromAddress = extractSuiteFromAddress(location.address);
  if (extractedFromAddress) return normalizeSuite(extractedFromAddress);
  return null;
}
var UNIT_DESIGNATORS = /* @__PURE__ */ new Set([
  "suite",
  "ste",
  "unit",
  "apt",
  "apartment",
  "building",
  "bldg",
  "room",
  "rm",
  "floor",
  "fl",
  "door",
  "dock",
  "lot",
  "gate",
  "bay",
  "hangar",
  "space",
  "spc",
  "trailer",
  "trlr",
  "no",
  "number",
  "num"
]);
function isUnitDesignator(value) {
  if (!value) return false;
  const tokens = value.toLowerCase().replace(/[.,;:()]/g, " ").split(/[\s/&]+/).map((t) => t.replace(/^#/, "").replace(/^-+|-+$/g, "")).filter(Boolean);
  if (!tokens.length) return /^\s*#\s*$/.test(value);
  const isIdentifier = (t) => t.length <= 6 && /\d/.test(t) && /^[a-z0-9-]+$/.test(t);
  return tokens.every((t, i) => {
    if (UNIT_DESIGNATORS.has(t) || isIdentifier(t) || t === "and") return true;
    if (!/^[a-z]{1,2}$/.test(t)) return false;
    const previous = tokens[i - 1];
    return tokens.length === 1 || previous !== void 0 && (UNIT_DESIGNATORS.has(previous) || isIdentifier(previous));
  });
}
function unitPrefixPattern() {
  const words = [...UNIT_DESIGNATORS].sort((a, b) => b.length - a.length).join("|");
  return new RegExp(`^(?:(?:${words})(?=[\\s.#\\d-]|$)\\.?|#)\\s*`, "i");
}
function unitFromAddressLineTwo(value) {
  const line = value?.trim();
  if (!line) return void 0;
  if (isUnitDesignator(line)) return line;
  const firstPart = line.split(",")[0].trim();
  if (firstPart && isUnitDesignator(firstPart)) return firstPart;
  return extractSuiteFromAddress(line) ?? void 0;
}
function streetLineOf(location) {
  const lineOne = location.address_line_one?.trim();
  if (lineOne) return lineOne;
  return (location.address ?? "").split(",")[0].trim();
}
function parseStreetLine(line) {
  const match = (line ?? "").trim().match(/^(\d+)(?:\s*-\s*(\d+))?([a-z])?\b[\s,]*(.*)$/i);
  if (!match) return null;
  const street = normalizeForComparison(match[4] ?? "");
  if (!street) return null;
  const [, first, second, letter] = match;
  const suffix = (letter ?? "").toLowerCase();
  if (second === void 0) return { number: first + suffix, street };
  const hyphenated = `${first}-${second}${suffix}`;
  const isRange = Number(second) > Number(first);
  return isRange ? { number: first + suffix, rangeEnd: Number(second), hyphenated, street } : { number: hyphenated, hyphenated, street };
}
function houseNumbersMatch(a, b) {
  if (a.hyphenated || b.hyphenated) {
    if (a.hyphenated && b.hyphenated) return a.hyphenated === b.hyphenated;
  } else if (a.number === b.number) {
    return true;
  }
  const within = (n, range) => range.rangeEnd !== void 0 && /^\d+$/.test(n) && Number(n) >= Number(range.number) && Number(n) <= range.rangeEnd;
  return within(a.number, b) || within(b.number, a);
}
var STREET_TYPES = /* @__PURE__ */ new Set([
  "street",
  "avenue",
  "boulevard",
  "drive",
  "lane",
  "road",
  "court",
  "circle",
  "place",
  "terrace",
  "highway",
  "parkway",
  "freeway",
  "expressway",
  "turnpike",
  "trail",
  "crossing",
  "plaza",
  "square",
  "center",
  "route",
  "alley",
  "bypass",
  "causeway",
  "way",
  "pike",
  "loop",
  "row",
  "path",
  "walk"
]);
var STREET_QUALIFIERS = /* @__PURE__ */ new Set([
  "north",
  "south",
  "east",
  "west",
  "northeast",
  "northwest",
  "southeast",
  "southwest",
  "ext",
  "extension",
  "extended",
  "frontage",
  "service",
  "spur",
  "connector",
  "business",
  "bypass",
  "loop"
]);
function isSameStreetAddress(a, b) {
  const left = parseStreetLine(a);
  const right = parseStreetLine(b);
  if (!left || !right) {
    const plainA = normalizeForComparison(a ?? "");
    return Boolean(plainA) && plainA === normalizeForComparison(b ?? "");
  }
  if (!houseNumbersMatch(left, right)) return false;
  const [shorter, longer] = left.street.length <= right.street.length ? [left.street, right.street] : [right.street, left.street];
  if (longer === shorter) return true;
  if (!longer.startsWith(`${shorter} `)) return false;
  const lastWord = shorter.split(" ").pop() ?? "";
  const nextWord = longer.slice(shorter.length + 1).split(" ")[0];
  return STREET_TYPES.has(lastWord) && !STREET_QUALIFIERS.has(nextWord);
}

// src/features/location-lookup/findByAlias.ts
async function findLocationByAlias(params) {
  const normalizedInput = normalizeInputForAlias(params.input);
  const aliases = await findLocationByInputAlias(
    { customerId: params.customerId, inputAlias: normalizedInput },
    { executor: params.executor }
  );
  return aliases?.[0]?.location ?? null;
}

// src/features/location-lookup/findByPlaceId.ts
async function findLocationByPlaceId(params) {
  const locations = await findLocationsByPlaceId(
    { customerId: params.customerId, placeId: params.placeId },
    { executor: params.executor }
  );
  return locations?.[0] ?? null;
}

// src/features/location-lookup/findByCustomerRef.ts
async function findLocationByCustomerRef(params) {
  const locations = await getLocationByCustomerRef(
    { customerId: params.customerId, customerRef: params.customerRef },
    { executor: params.executor }
  );
  return locations?.[0] ?? null;
}

// src/features/location-lookup/findNearby.ts
import { calculateDistanceMeters } from "@hopdrive/sdk-core";

// src/utils/similarity.ts
import { distance as levenshteinDistance } from "fastest-levenshtein";
function levenshteinRatio(a, b) {
  if (a === b) return 1;
  if (a.length === 0 || b.length === 0) return 0;
  const dist = levenshteinDistance(a, b);
  const maxLen = Math.max(a.length, b.length);
  return 1 - dist / maxLen;
}

// src/utils/scoring.ts
var NEARBY_CANDIDATE_LIMIT = 10;
var DEFAULT_MATCHING_CONFIG = {
  PROXIMITY_RADIUS_METERS: 50,
  MATCH_THRESHOLD: 0.7,
  NAME_SIMILARITY_WEIGHT: 0.4,
  ADDRESS_SIMILARITY_WEIGHT: 0.3,
  DISTANCE_WEIGHT: 0.3,
  SUITE_MISMATCH_PENALTY: 0.5
};
function scoreCandidate(candidate, input, config = DEFAULT_MATCHING_CONFIG) {
  const inputNameNorm = input.name ? normalizeNameForComparison(input.name) : "";
  const candidateNameNorm = normalizeNameForComparison(candidate.name || "");
  const inputAddressNorm = normalizeForComparison(input.streetAddress);
  const candidateAddressNorm = normalizeForComparison(candidate.address_line_one || candidate.address || "");
  const hasNameSignal = Boolean(inputNameNorm && candidateNameNorm);
  const nameSimilarity = hasNameSignal ? levenshteinRatio(inputNameNorm, candidateNameNorm) : 0;
  const nameWeight = hasNameSignal ? config.NAME_SIMILARITY_WEIGHT : 0;
  const inputStreet = parseStreetLine(input.streetAddress);
  const candidateStreet = parseStreetLine(streetLineOf(candidate));
  const differentHouseNumber = Boolean(inputStreet && candidateStreet && !houseNumbersMatch(inputStreet, candidateStreet));
  const addressSimilarity = differentHouseNumber ? 0 : levenshteinRatio(inputAddressNorm, candidateAddressNorm);
  const distanceScore = 1 - candidate.distance_meters / config.PROXIMITY_RADIUS_METERS;
  let suitePenalty = 0;
  const inputSuite = normalizeSuite(input.addressLineTwo);
  const candidateSuite = getEffectiveSuite(candidate);
  if (inputSuite && candidateSuite && inputSuite !== candidateSuite) {
    suitePenalty = config.SUITE_MISMATCH_PENALTY;
  }
  const totalWeight = nameWeight + config.ADDRESS_SIMILARITY_WEIGHT + config.DISTANCE_WEIGHT;
  const weighted = nameSimilarity * nameWeight + addressSimilarity * config.ADDRESS_SIMILARITY_WEIGHT + distanceScore * config.DISTANCE_WEIGHT;
  const score = (totalWeight > 0 ? weighted / totalWeight : 0) - suitePenalty;
  return {
    location: candidate,
    score: Math.max(0, Math.min(1, score)),
    distanceMeters: candidate.distance_meters
  };
}
function scoreCandidates(candidates, input, config = DEFAULT_MATCHING_CONFIG) {
  return candidates.map((candidate) => scoreCandidate(candidate, input, config)).sort((a, b) => b.score - a.score);
}
function findExactAddressMatch(candidates, input) {
  if (!normalizeForComparison(input.streetAddress ?? "")) return void 0;
  const inputSuite = normalizeSuite(input.addressLineTwo);
  const matches = candidates.filter((c) => isSameStreetAddress(input.streetAddress, streetLineOf(c))).filter((c) => {
    const suite = getEffectiveSuite(c);
    return !(inputSuite && suite && inputSuite !== suite);
  });
  return matches.length === 1 ? matches[0] : void 0;
}

// src/features/location-lookup/findNearby.ts
async function findLocationsNearby2(params) {
  const radiusMeters = params.radiusMeters ?? DEFAULT_MATCHING_CONFIG.PROXIMITY_RADIUS_METERS;
  const point = {
    type: "Point",
    coordinates: [params.lng, params.lat]
    // GeoJSON uses [longitude, latitude]
  };
  const result = await findLocationsNearby(
    {
      customerId: params.customerId,
      point,
      radiusMeters
    },
    { executor: params.executor }
  );
  const locations = result ?? [];
  return locations.map((location) => ({
    ...location,
    distance_meters: calculateDistanceMeters(
      params.lat,
      params.lng,
      location.latitude ?? 0,
      location.longitude ?? 0
    )
  }));
}

// src/features/geocoding/geocodeAddress.ts
import { resolveExecutor as resolveExecutor3, getLogger as getLogger3 } from "@hopdrive/sdk-core";

// src/features/geocoding/findGeocodeCache.ts
async function findGeocodeCache2(params) {
  const normalizedAddress = normalizeInputForAlias(params.address);
  const geocodeCache = await findGeocodeCache(
    { normalizedAddress },
    { executor: params.executor }
  );
  const entry = geocodeCache?.[0];
  if (!entry) {
    return null;
  }
  const expiresAt = new Date(entry.expires_at);
  if (expiresAt <= /* @__PURE__ */ new Date()) {
    return null;
  }
  return entry.geocode_result;
}

// src/features/geocoding/storeGeocodeCache.ts
import { getLogger as getLogger2 } from "@hopdrive/sdk-core";
async function storeGeocodeCache2(params) {
  const log = params.logger ?? getLogger2();
  const normalizedAddress = normalizeInputForAlias(params.address);
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1e3);
  try {
    await storeGeocodeCache(
      {
        normalizedAddress,
        geocodeResult: params.geocodeResult,
        expiresAt: expiresAt.toISOString()
      },
      { executor: params.executor }
    );
  } catch (err) {
    const error = err;
    log.warn("lanes.geocoding.storeCacheFailed", {
      address: params.address,
      normalizedAddress,
      error: error.message
    });
  }
}
function storeGeocodeCacheAsync(params) {
  storeGeocodeCache2(params).catch(() => {
  });
}

// src/features/geocoding/geocodeAddress.ts
async function geocodeAddress(address, executor, placeId, options) {
  const exec = resolveExecutor3(executor);
  const logger = options?.logger;
  const log = logger ?? getLogger3();
  const cached = await findGeocodeCache2({ address, executor: exec });
  if (cached) {
    log.debug("geocode.cacheHit", { address });
    return cached;
  }
  const { geocoding } = getDefaultGeoProviders();
  const apiStart = Date.now();
  let geoResult;
  if (placeId && geocoding.fetchPlaceDetails) {
    geoResult = logger ? await geocoding.fetchPlaceDetails(placeId, { logger }) : await geocoding.fetchPlaceDetails(placeId);
  } else {
    geoResult = await geocoding.geocode({ address, ...logger && { logger } });
  }
  log.debug("geocode.apiCall", { address, placeId, apiMs: Date.now() - apiStart });
  const result = mapGeoResultToLocal(geoResult);
  storeGeocodeCacheAsync({
    address,
    geocodeResult: result,
    executor: exec,
    ...logger && { logger }
  });
  return result;
}
function mapGeoResultToLocal(geoResult) {
  return {
    streetAddress: geoResult.streetAddress,
    formattedAddress: geoResult.formattedAddress,
    latitude: geoResult.latitude,
    longitude: geoResult.longitude,
    placeId: geoResult.placeId,
    plusCode: geoResult.plusCode,
    components: {
      streetNumber: geoResult.components.streetNumber,
      route: geoResult.components.route,
      city: geoResult.components.city,
      state: geoResult.components.state,
      postalCode: geoResult.components.postalCode,
      country: geoResult.components.country,
      subpremise: geoResult.components.subpremise
    }
  };
}

// src/features/geocoding/generatePlusCode.ts
import { OpenLocationCode } from "open-location-code";
var plusCodeGenerator = new OpenLocationCode();
function generatePlusCode(lat, lng) {
  return plusCodeGenerator.encode(lat, lng);
}

// src/features/resolve-location/createLocation.ts
import { getLogger as getLogger4 } from "@hopdrive/sdk-core";
import { logEvent } from "@hopdrive/sdk-events";

// src/validation/location-schemas.ts
import { z } from "zod";
var ResolveLocationInputSchema = z.object({
  customerId: z.number().int().positive(),
  address: z.string().min(1).max(500),
  name: z.string().max(255).optional(),
  addressLineTwo: z.string().max(100).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  customerRef: z.string().max(100).optional(),
  resolution: z.object({
    autoResolveThreshold: z.number().min(0).max(1).optional(),
    autoCreate: z.boolean().optional(),
    lookupOnly: z.boolean().optional(),
    forceCreate: z.boolean().optional()
  }).optional()
});
var CreateLocationInputSchema = z.object({
  customerId: z.number().int().positive(),
  name: z.string().min(1).max(255),
  address: z.string().min(1).max(500),
  addressLineOne: z.string().max(255).optional(),
  addressLineTwo: z.string().max(100).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  zipCode: z.string().max(20).optional(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  placeId: z.string().optional(),
  plusCode: z.string().max(20).optional(),
  regionId: z.number().int().positive().optional(),
  timezone: z.string().optional(),
  type: z.enum(["customer", "consumer residential", "consumer business", "driver", "driver home"]).optional(),
  customerRef: z.string().max(100).optional(),
  coordinatesSource: z.enum(["geocoded", "user_adjusted", "imported"]).optional(),
  geocodedLatitude: z.number().min(-90).max(90).optional(),
  geocodedLongitude: z.number().min(-180).max(180).optional(),
  placeIdAuthoritative: z.boolean().optional()
});

// src/logging.ts
import { withLogContext } from "@hopdrive/sdk-core";
function generateOperationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().slice(0, 8);
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
function scopeLogger(logger, labels) {
  if ("withMetadata" in logger && typeof logger.withMetadata === "function") {
    return logger.withMetadata(labels);
  }
  return withLogContext(logger, labels);
}
function elapsed(startedAt) {
  return Date.now() - startedAt;
}

// src/features/resolve-location/createLocation.ts
async function createLocation2(params) {
  const parseResult = CreateLocationInputSchema.safeParse(params);
  if (!parseResult.success) {
    throw new LocationValidationError(parseResult.error);
  }
  const log = scopeLogger(params.logger ?? getLogger4(), {
    operationId: generateOperationId(),
    operation: "createLocation"
  });
  let region = null;
  try {
    region = await getRegionByCoordinates(
      {
        point: {
          type: "Point",
          coordinates: [params.longitude, params.latitude],
          crs: {
            type: "name",
            properties: { name: "EPSG:4326" }
          }
        }
      },
      { executor: params.executor }
    );
  } catch (error) {
    log.error("createLocation.region", { error });
  }
  let timezone = params.timezone;
  if (!timezone && params.latitude && params.longitude) {
    try {
      const location2 = { latitude: params.latitude, longitude: params.longitude };
      const { timezone: timezoneProvider } = getDefaultGeoProviders();
      const tz = params.logger ? await timezoneProvider.resolveTimezone(location2, { logger: params.logger }) : await timezoneProvider.resolveTimezone(location2);
      if (tz) {
        timezone = tz.timeZoneId;
      }
    } catch (error) {
      log.warn("createLocation.timezoneResolution", { error });
    }
  }
  const location = await createLocation(
    {
      location: {
        customer_id: params.customerId,
        name: params.name,
        address: params.address,
        address_line_one: params.addressLineOne,
        address_line_two: params.addressLineTwo,
        city: params.city,
        state: params.state,
        zip_code: params.zipCode,
        latitude: params.latitude,
        longitude: params.longitude,
        place_id: params.placeId,
        plus_code: params.plusCode,
        region_id: params.regionId ?? region?.[0]?.id ?? null,
        timezone,
        type: params.type ?? "customer",
        customer_ref: params.customerRef,
        coordinates_source: params.coordinatesSource ?? "geocoded",
        geocoding_source: params.geocodingSource ?? "google_geocoding",
        // Always populate geocoded coordinates - default to lat/lon if not explicitly provided
        geocoded_latitude: params.geocodedLatitude ?? params.latitude,
        geocoded_longitude: params.geocodedLongitude ?? params.longitude,
        geocoded_at: (/* @__PURE__ */ new Date()).toISOString(),
        place_id_authoritative: params.placeIdAuthoritative ?? true,
        active: 1
      }
    },
    { executor: params.executor }
  );
  logEvent("location.created", {
    user: "system",
    role: "system",
    location_id: location.id,
    customer_id: params.customerId,
    metadata: {
      event: {
        name: params.name,
        address: params.address,
        placeId: params.placeId,
        coordinatesSource: params.coordinatesSource ?? "geocoded"
      }
    },
    fireAndForget: true
  });
  return location;
}

// src/features/resolve-location/recordInputAlias.ts
import { getLogger as getLogger5 } from "@hopdrive/sdk-core";
async function recordInputAlias2(params) {
  const normalizedInput = normalizeInputForAlias(params.input);
  try {
    await recordInputAlias(
      {
        locationId: params.locationId,
        customerId: params.customerId,
        inputAlias: normalizedInput
      },
      { executor: params.executor }
    );
  } catch (err) {
    const error = err;
    if (error?.extensions?.code !== "constraint-violation") {
      const log = params.logger ?? getLogger5();
      log.warn("recordInputAlias.failed", {
        locationId: params.locationId,
        error: error.message
      });
    }
  }
}
function recordInputAliasAsync(params) {
  recordInputAlias2(params).catch(() => {
  });
}

// src/features/resolve-location/resolveLocation.ts
async function resolveLocation(params) {
  const executor = resolveExecutor4(params.executor);
  const autoResolveThreshold = params.resolution?.autoResolveThreshold ?? 0.85;
  const autoCreate = params.resolution?.autoCreate ?? true;
  const lookupOnly = params.resolution?.lookupOnly ?? false;
  const name = params.name?.trim() || void 0;
  const rawAddressLineTwo = params.addressLineTwo?.trim() || void 0;
  const suppliedAddressLineTwo = unitFromAddressLineTwo(rawAddressLineTwo);
  const logger = params.logger;
  const log = scopeLogger(logger ?? getLogger6(), {
    operationId: generateOperationId(),
    operation: "resolveLocation",
    customerId: params.customerId,
    ...params.correlationId && { correlationId: params.correlationId }
  });
  const startedAt = Date.now();
  log.info("resolveLocation.start", { address: params.address });
  if (rawAddressLineTwo && rawAddressLineTwo !== suppliedAddressLineTwo) {
    log.info("resolveLocation.addressLineTwoNote", { addressLineTwo: rawAddressLineTwo, unit: suppliedAddressLineTwo ?? null });
  }
  if (params.customerRef) {
    const refMatch = await findLocationByCustomerRef({
      customerId: params.customerId,
      customerRef: params.customerRef,
      executor
    });
    if (refMatch) {
      log.info("resolveLocation.resolved", { method: "customer_ref", locationId: refMatch.id, elapsedMs: elapsed(startedAt) });
      return {
        resolved: true,
        location: refMatch,
        matchMethod: "customer_ref",
        matchScore: 1
      };
    }
  }
  const aliasMatch = await findLocationByAlias({
    customerId: params.customerId,
    input: params.address,
    executor
  });
  if (aliasMatch) {
    log.info("resolveLocation.resolved", { method: "input_alias", locationId: aliasMatch.id, elapsedMs: elapsed(startedAt) });
    return {
      resolved: true,
      location: aliasMatch,
      matchMethod: "input_alias",
      matchScore: 1
    };
  }
  const geocodeResult = logger ? await geocodeAddress(params.address, executor, params.placeId, { logger }) : await geocodeAddress(params.address, executor, params.placeId);
  const addressLineTwo = suppliedAddressLineTwo ?? geocodeResult.components.subpremise ?? void 0;
  const storedAddressLineTwo = (rawAddressLineTwo && !suppliedAddressLineTwo && addressLineTwo ? `${addressLineTwo}, ${rawAddressLineTwo}` : rawAddressLineTwo ?? addressLineTwo)?.slice(0, 100);
  const geocoded = {
    streetAddress: geocodeResult.streetAddress,
    formattedAddress: geocodeResult.formattedAddress,
    lat: geocodeResult.latitude,
    lng: geocodeResult.longitude,
    placeId: geocodeResult.placeId,
    plusCode: geocodeResult.plusCode
  };
  const [placeIdMatch, proximityCandidates] = await Promise.all([
    findLocationByPlaceId({
      customerId: params.customerId,
      placeId: geocodeResult.placeId,
      executor
    }),
    findLocationsNearby2({
      customerId: params.customerId,
      lat: geocodeResult.latitude,
      lng: geocodeResult.longitude,
      executor
    })
  ]);
  if (placeIdMatch) {
    const inputSuite = normalizeSuite(addressLineTwo);
    const existingSuite = getEffectiveSuite(placeIdMatch);
    const suiteConflict = inputSuite && existingSuite && inputSuite !== existingSuite;
    if (!suiteConflict) {
      recordInputAliasAsync({
        locationId: placeIdMatch.id,
        customerId: params.customerId,
        input: params.address,
        executor,
        ...logger && { logger }
      });
      log.info("resolveLocation.resolved", { method: "place_id", locationId: placeIdMatch.id, elapsedMs: elapsed(startedAt) });
      return {
        resolved: true,
        location: placeIdMatch,
        matchMethod: "place_id",
        matchScore: 1,
        geocoded
      };
    }
    log.info("resolveLocation.placeIdSuiteConflict", {
      placeId: geocodeResult.placeId,
      inputSuite: addressLineTwo,
      existingSuite: placeIdMatch.address_line_two,
      elapsedMs: elapsed(startedAt)
    });
  }
  if (params.resolution?.forceCreate && autoCreate && !lookupOnly) {
    const newLocation = await createLocation2({
      customerId: params.customerId,
      name: name ?? geocodeResult.formattedAddress,
      address: geocodeResult.formattedAddress,
      addressLineOne: geocodeResult.streetAddress,
      addressLineTwo: storedAddressLineTwo,
      city: geocodeResult.components.city,
      state: geocodeResult.components.state,
      zipCode: geocodeResult.components.postalCode,
      latitude: geocodeResult.latitude,
      longitude: geocodeResult.longitude,
      placeId: geocodeResult.placeId,
      plusCode: geocodeResult.plusCode,
      coordinatesSource: "geocoded",
      placeIdAuthoritative: true,
      executor,
      ...logger && { logger }
    });
    recordInputAliasAsync({
      locationId: newLocation.id,
      customerId: params.customerId,
      input: params.address,
      executor,
      ...logger && { logger }
    });
    logIfExistingRow(log, newLocation);
    log.info("resolveLocation.forceCreated", { locationId: newLocation.id, elapsedMs: elapsed(startedAt) });
    return { resolved: true, location: newLocation, created: true, geocoded };
  }
  const nearbyTruncated = proximityCandidates.length > NEARBY_CANDIDATE_LIMIT;
  const nearbyCandidates = proximityCandidates.slice(0, NEARBY_CANDIDATE_LIMIT);
  const scoredCandidates = scoreCandidates(
    nearbyCandidates,
    {
      name,
      streetAddress: geocodeResult.streetAddress,
      addressLineTwo
    }
  );
  const bestCandidate = scoredCandidates[0];
  if (bestCandidate && bestCandidate.score >= autoResolveThreshold) {
    recordInputAliasAsync({
      locationId: bestCandidate.location.id,
      customerId: params.customerId,
      input: params.address,
      executor,
      ...logger && { logger }
    });
    log.info("resolveLocation.resolved", {
      method: "proximity",
      locationId: bestCandidate.location.id,
      score: bestCandidate.score,
      candidateCount: scoredCandidates.length,
      elapsedMs: elapsed(startedAt)
    });
    return {
      resolved: true,
      location: bestCandidate.location,
      matchMethod: "proximity",
      matchScore: bestCandidate.score,
      candidates: scoredCandidates,
      geocoded
    };
  }
  const exactMatch = nearbyTruncated ? void 0 : findExactAddressMatch(nearbyCandidates, {
    streetAddress: geocodeResult.streetAddress,
    addressLineTwo
  });
  if (exactMatch) {
    const matchScore = scoredCandidates.find((c) => c.location.id === exactMatch.id)?.score ?? 0;
    recordInputAliasAsync({
      locationId: exactMatch.id,
      customerId: params.customerId,
      input: params.address,
      executor,
      ...logger && { logger }
    });
    log.info("resolveLocation.resolved", {
      method: "exact_address",
      locationId: exactMatch.id,
      score: matchScore,
      distanceMeters: Math.round(exactMatch.distance_meters),
      candidateCount: scoredCandidates.length,
      elapsedMs: elapsed(startedAt)
    });
    return {
      resolved: true,
      location: exactMatch,
      matchMethod: "exact_address",
      matchScore,
      candidates: scoredCandidates,
      geocoded
    };
  }
  const hasPlausibleMatch = scoredCandidates.some(
    (c) => c.score >= DEFAULT_MATCHING_CONFIG.MATCH_THRESHOLD
  );
  if (!hasPlausibleMatch && autoCreate && !lookupOnly) {
    const newLocation = await createLocation2({
      customerId: params.customerId,
      name: name ?? geocodeResult.formattedAddress,
      address: geocodeResult.formattedAddress,
      addressLineOne: geocodeResult.streetAddress,
      addressLineTwo: storedAddressLineTwo,
      city: geocodeResult.components.city,
      state: geocodeResult.components.state,
      zipCode: geocodeResult.components.postalCode,
      latitude: geocodeResult.latitude,
      longitude: geocodeResult.longitude,
      placeId: geocodeResult.placeId,
      plusCode: geocodeResult.plusCode,
      coordinatesSource: "geocoded",
      placeIdAuthoritative: true,
      executor,
      ...logger && { logger }
    });
    recordInputAliasAsync({
      locationId: newLocation.id,
      customerId: params.customerId,
      input: params.address,
      executor,
      ...logger && { logger }
    });
    logIfExistingRow(log, newLocation);
    log.info("resolveLocation.created", { locationId: newLocation.id, elapsedMs: elapsed(startedAt) });
    return {
      resolved: true,
      location: newLocation,
      created: true,
      geocoded
    };
  }
  log.warn("resolveLocation.unresolved", {
    candidateCount: scoredCandidates.length,
    bestScore: bestCandidate?.score ?? null,
    elapsedMs: elapsed(startedAt)
  });
  return {
    resolved: false,
    candidates: scoredCandidates.filter(
      (c) => c.score >= DEFAULT_MATCHING_CONFIG.MATCH_THRESHOLD
    ),
    geocoded
  };
}
function logIfExistingRow(log, location) {
  const createdAt = location.createdat ? Date.parse(location.createdat) : NaN;
  if (Number.isFinite(createdAt) && Date.now() - createdAt > 6e4) {
    log.warn("resolveLocation.createMatchedExistingRow", { locationId: location.id, createdat: location.createdat });
  }
}

// src/features/resolve-lane/resolveLane.ts
import { getLogger as getLogger7 } from "@hopdrive/sdk-core";

// src/features/resolve-lane/createLaneShell.ts
import { logEvent as logEvent2 } from "@hopdrive/sdk-events";

// src/validation/lane-schemas.ts
import { z as z2 } from "zod";
var ResolveLaneInputSchema = z2.object({
  customerId: z2.number().int().positive(),
  originLocationId: z2.number().int().positive(),
  destinationLocationId: z2.number().int().positive()
});
var CreateLaneShellInputSchema = z2.object({
  customerId: z2.number().int().positive(),
  originLocationId: z2.number().int().positive(),
  destinationLocationId: z2.number().int().positive(),
  description: z2.string().max(255).optional()
});

// src/utils/locationDisplayName.ts
function locationDisplayName(loc) {
  const name = loc.name?.trim();
  if (name) return name;
  return loc.address.split(",")[0].trim();
}

// src/features/resolve-lane/createLaneShell.ts
async function createLaneShell(params) {
  const parseResult = CreateLaneShellInputSchema.safeParse(params);
  if (!parseResult.success) {
    throw new LaneValidationError(parseResult.error);
  }
  if (params.originLocationId === params.destinationLocationId) {
    throw new DuplicateLocationError(params.originLocationId);
  }
  let forwardDescription;
  let inverseDescription;
  if (params.description) {
    forwardDescription = params.description;
    inverseDescription = reverseDescription(params.description);
  } else {
    const [origin, destination] = await Promise.all([
      getLocation({ id: params.originLocationId, required: true, executor: params.executor }),
      getLocation({ id: params.destinationLocationId, required: true, executor: params.executor })
    ]);
    const originName = locationDisplayName(origin);
    const destName = locationDisplayName(destination);
    forwardDescription = `${originName} to ${destName}`;
    inverseDescription = `${destName} to ${originName}`;
  }
  const insertResult = await createLaneShellPair(
    {
      // active is a soft-delete flag (1 = live, 0 = deleted), not a lifecycle
      // gate — readiness is expressed by status. New shells are live from birth.
      forwardLane: {
        customer_id: params.customerId,
        origin_location_id: params.originLocationId,
        destination_location_id: params.destinationLocationId,
        description: forwardDescription,
        status: "new",
        active: 1
      },
      inverseLane: {
        customer_id: params.customerId,
        origin_location_id: params.destinationLocationId,
        destination_location_id: params.originLocationId,
        description: inverseDescription,
        status: "new",
        active: 1
      }
    },
    { executor: params.executor }
  );
  const forwardLane = insertResult.forward;
  const inverseLane = insertResult.inverse;
  await triggerLaneEnrichment(
    { ids: [forwardLane.id, inverseLane.id] },
    { executor: params.executor }
  );
  logEvent2("lane.created", {
    user: "system",
    role: "system",
    lane_id: forwardLane.id,
    customer_id: params.customerId,
    metadata: {
      event: {
        forwardLaneId: forwardLane.id,
        inverseLaneId: inverseLane.id,
        originLocationId: params.originLocationId,
        destinationLocationId: params.destinationLocationId
      }
    },
    fireAndForget: true
  });
  return {
    forward: { ...forwardLane, status: "pending" },
    inverse: { ...inverseLane, status: "pending" }
  };
}
function reverseDescription(description) {
  const parts = description.split(" to ");
  return parts.length === 2 ? `${parts[1]} to ${parts[0]}` : description;
}

// src/features/resolve-lane/resolveLane.ts
async function resolveLane(params) {
  const log = scopeLogger(params.logger ?? getLogger7(), {
    operationId: generateOperationId(),
    operation: "resolveLane",
    customerId: params.customerId,
    ...params.correlationId && { correlationId: params.correlationId }
  });
  const startedAt = Date.now();
  log.debug("resolveLane.start", {
    customerId: params.customerId,
    originLocationId: params.originLocationId,
    destinationLocationId: params.destinationLocationId
  });
  const lanes = await resolveLaneByLocations(
    {
      customerId: params.customerId,
      originLocationId: params.originLocationId,
      destinationLocationId: params.destinationLocationId
    },
    { executor: params.executor }
  );
  const existingLane = lanes?.[0];
  if (existingLane) {
    log.info("resolveLane.found", { laneId: existingLane.id, elapsedMs: elapsed(startedAt) });
    return {
      lane: existingLane,
      inverse: existingLane.inverse,
      created: false
    };
  }
  let lanePair;
  try {
    lanePair = await createLaneShell({
      customerId: params.customerId,
      originLocationId: params.originLocationId,
      destinationLocationId: params.destinationLocationId,
      executor: params.executor
    });
  } catch (error) {
    const racedLanes = await resolveLaneByLocations(
      {
        customerId: params.customerId,
        originLocationId: params.originLocationId,
        destinationLocationId: params.destinationLocationId
      },
      { executor: params.executor }
    );
    const racedLane = racedLanes?.[0];
    if (!racedLane) throw error;
    log.info("resolveLane.raceRecovered", { laneId: racedLane.id, elapsedMs: elapsed(startedAt) });
    return {
      lane: racedLane,
      inverse: racedLane.inverse,
      created: false
    };
  }
  log.info("resolveLane.created", {
    forwardId: lanePair.forward.id,
    inverseId: lanePair.inverse?.id,
    elapsedMs: elapsed(startedAt)
  });
  return {
    lane: lanePair.forward,
    inverse: lanePair.inverse,
    created: true
  };
}

// src/features/resolve-lane/resolveLaneWithRouting.ts
import { getLogger as getLogger12 } from "@hopdrive/sdk-core";

// src/features/enrichment/enrichLane.ts
import { resolveExecutor as resolveExecutor6, getLogger as getLogger11 } from "@hopdrive/sdk-core";
import { getCustomerConfig } from "@hopdrive/sdk-configs";
import { logEvent as logEvent4 } from "@hopdrive/sdk-events";

// src/features/lane-lookup/getLane.ts
async function getLane(params) {
  let lane = null;
  if (params.includeLocations) {
    lane = await getLaneWithLocations(
      { id: params.id },
      { executor: params.executor }
    );
  } else if (params.includeInverse) {
    lane = await getLaneWithInverse(
      { id: params.id },
      { executor: params.executor }
    );
  } else {
    lane = await getLaneById(
      { id: params.id },
      { executor: params.executor }
    );
  }
  if (!lane && params.required) {
    throw new LaneNotFoundError({ id: params.id });
  }
  return lane ?? null;
}

// src/features/enrichment/updateLaneEnrichment.ts
async function updateLaneEnrichment2(params) {
  const {
    laneId,
    description,
    distanceMiles,
    durationSeconds,
    averageDriveSpeedMph,
    averageDriveSpeedMinPerMile,
    pickupInspectionSec,
    deliveryInspectionSec,
    returnRideWaitSec,
    tolls,
    polyline,
    routeMetadata,
    enrichmentSource,
    creationCriteria,
    status
  } = params;
  const enrichedAt = (/* @__PURE__ */ new Date()).toISOString();
  const updated = await updateLaneEnrichment(
    {
      id: laneId,
      description,
      distanceMiles,
      durationSec: durationSeconds,
      averageDriveSpeedMph,
      averageDriveSpeedMinPerMile,
      pickupInspectionSec,
      deliveryInspectionSec,
      returnRideWaitSec,
      tolls,
      routePolyline: polyline,
      routeMetadata,
      enrichmentSource,
      creationCriteria,
      status,
      enrichedAt
    },
    { executor: params.executor }
  );
  if (!updated) {
    throw new Error(`Failed to update lane ${laneId}: lane not found`);
  }
  return {
    laneId: updated.id,
    affectedRows: 1,
    updatedat: updated.updatedat,
    status: updated.status
  };
}

// src/features/lane-update/updateLaneStatus.ts
import { getLogger as getLogger8 } from "@hopdrive/sdk-core";
var VALID_STATUSES = ["new", "pending", "enriching", "ready", "failed", "stale"];
async function updateLaneStatus2(params) {
  const { laneId, status } = params;
  const log = params.logger ?? getLogger8();
  if (!VALID_STATUSES.includes(status)) {
    throw new Error(`Invalid lane status: ${status}. Must be one of: ${VALID_STATUSES.join(", ")}`);
  }
  const ids = Array.isArray(laneId) ? laneId : [laneId];
  if (ids.length === 0) {
    return { affectedRows: 0, updated: [] };
  }
  if (params.unchangedAt !== void 0 && !params.from) {
    throw new Error("updateLaneStatus: unchangedAt requires from");
  }
  if (params.from) {
    return updateGuarded(ids, status, params.from, params.unchangedAt, params.executor, log);
  }
  if (status === "ready") {
    const enrichedAt = (/* @__PURE__ */ new Date()).toISOString();
    const updateLanes2 = await updateLaneStatusReady(
      { ids, enrichedAt },
      { executor: params.executor }
    );
    const updated2 = (updateLanes2?.returning ?? []).map((lane) => ({
      id: lane.id,
      status: lane.status,
      updatedat: lane.updatedat,
      enrichedAt: lane.enriched_at
    }));
    log.debug("laneStatus.updated", { laneId, status });
    return {
      affectedRows: updateLanes2?.affected_rows ?? 0,
      updated: updated2
    };
  }
  const updateLanes = await updateLaneStatus(
    { ids, status },
    { executor: params.executor }
  );
  const updated = (updateLanes?.returning ?? []).map((lane) => ({
    id: lane.id,
    status: lane.status,
    updatedat: lane.updatedat
  }));
  log.debug("laneStatus.updated", { laneId, status });
  return {
    affectedRows: updateLanes?.affected_rows ?? 0,
    updated
  };
}
async function updateGuarded(ids, status, from, unchangedAt, executor, log) {
  const named = from.filter((s) => s !== null);
  for (const source of named) {
    if (!VALID_STATUSES.includes(source)) {
      throw new Error(`Invalid lane source status: ${source}. Must be one of: ${VALID_STATUSES.join(", ")}`);
    }
  }
  const sources = [];
  if (named.length > 0) sources.push({ status: { _in: named } });
  if (from.includes(null)) sources.push({ status: { _is_null: true } });
  if (sources.length === 0) {
    return { affectedRows: 0, updated: [] };
  }
  const set = status === "ready" ? { status, enriched_at: (/* @__PURE__ */ new Date()).toISOString(), updatedat: "now()" } : { status, updatedat: "now()" };
  const where = { id: { _in: ids }, _or: sources };
  if (unchangedAt !== void 0) where.updatedat = { _eq: unchangedAt };
  const updateLanes = await updateLaneStatusGuarded({ where, set }, { executor });
  const updated = (updateLanes?.returning ?? []).map((lane) => ({
    id: lane.id,
    status: lane.status,
    updatedat: lane.updatedat,
    ...status === "ready" && { enrichedAt: lane.enriched_at }
  }));
  const affectedRows = updateLanes?.affected_rows ?? 0;
  log.debug("laneStatus.updated", { laneId: ids, status, from, unchangedAt, affectedRows });
  return { affectedRows, updated };
}

// src/features/lane-update/updateLaneDescription.ts
import { getLogger as getLogger9 } from "@hopdrive/sdk-core";
async function updateLaneDescription2(params) {
  const { laneId, description } = params;
  const updated = await updateLaneDescription(
    { id: laneId, description },
    { executor: params.executor }
  );
  if (!updated) {
    throw new Error(`Failed to update lane ${laneId}: lane not found`);
  }
  const log = params.logger ?? getLogger9();
  log.debug("laneDescription.updated", { laneId });
  return {
    laneId: updated.id,
    description: updated.description ?? null,
    updatedat: updated.updatedat
  };
}

// src/features/enrichment/rerunLane.ts
import { getLogger as getLogger10, resolveExecutor as resolveExecutor5 } from "@hopdrive/sdk-core";

// src/features/enrichment/logLaneEnrichmentRequested.ts
import { logEvent as logEvent3 } from "@hopdrive/sdk-events";
function laneEnrichmentSnapshot(lane) {
  return {
    status: lane.status ?? null,
    distance_miles: lane.distance_miles ?? null,
    duration_seconds: lane.duration_sec ?? null,
    tolls: lane.tolls ?? null,
    enrichment_source: lane.enrichment_source ?? null,
    enriched_at: lane.enriched_at ?? null
  };
}
function logLaneEnrichmentRequested(params) {
  const { laneId, user, role, customerId, snapshot, metadata, executor } = params;
  return logEvent3("lane.enrichment.requested", {
    user,
    role,
    lane_id: laneId,
    ...customerId !== void 0 && { customer_id: customerId },
    metadata: { ...metadata != null && typeof metadata === "object" ? metadata : {}, snapshot },
    executor,
    safe: true
  });
}

// src/features/enrichment/rerunLane.ts
var LANE_STUCK_AFTER_MS = 1e4;
var SOURCE_STATUS = {
  "not-started": "new",
  "stuck-pending": "pending",
  "stuck-enriching": "enriching"
};
function laneNeedsRerun(lane, unchangedForMs) {
  if (lane.status === "new") return "not-started";
  if (lane.status !== "pending" && lane.status !== "enriching") return null;
  if (!lane.updatedat || unchangedForMs < LANE_STUCK_AFTER_MS) return null;
  return lane.status === "pending" ? "stuck-pending" : "stuck-enriching";
}
function laneUnchangedForMs(updatedat, now = Date.now()) {
  const at = updatedat ? Date.parse(updatedat) : NaN;
  return Number.isFinite(at) ? Math.max(0, now - at) : 0;
}
function rerunTransition(reason, unchangedAt) {
  if (reason === "not-started") return [{ status: "pending", from: ["new"] }];
  if (!unchangedAt) return [];
  if (reason === "stuck-pending") {
    return [
      { status: "new", from: ["pending"], unchangedAt },
      { status: "pending", from: ["new"] }
    ];
  }
  return [{ status: "pending", from: ["enriching"], unchangedAt }];
}
async function rerunLane(params) {
  const { lane, reason, customerId, user, role } = params;
  const executor = resolveExecutor5(params.executor);
  const log = params.logger ?? getLogger10();
  const writes = lane.status === SOURCE_STATUS[reason] ? rerunTransition(reason, lane.updatedat) : [];
  const landed = await performWrites(lane.id, writes, executor, params.logger);
  const rerun = writes.length > 0 && landed.count === writes.length;
  if (rerun) {
    await logLaneEnrichmentRequested({
      laneId: lane.id,
      user,
      role,
      customerId,
      snapshot: laneEnrichmentSnapshot(lane),
      metadata: { reason },
      executor
    });
  }
  log.info("rerunLane.complete", { laneId: lane.id, reason, rerun, writes: writes.length, landed: landed.count });
  return { rerun, reason, status: landed.last?.status ?? null, updatedat: landed.last?.updatedat ?? null };
}
async function performWrites(laneId, writes, executor, logger) {
  let count = 0;
  let last = null;
  for (const write of writes) {
    const result = await updateLaneStatus2({ laneId, ...write, executor, ...logger && { logger } });
    const row = result.updated[0];
    if (result.affectedRows === 0 || !row) break;
    count += 1;
    last = { status: row.status, updatedat: row.updatedat };
  }
  return { count, last };
}

// src/features/enrichment/enrichLane.ts
var METERS_TO_MILES = 621371e-9;
var PICKUP_DWELL_TIME = 300;
var PICKUP_INSPECTION_TIME = 300;
var DELIVERY_DWELL_TIME = 300;
var DELIVERY_INSPECTION_TIME = 180;
var PICKUP_INSPECTION_SEC = PICKUP_DWELL_TIME + PICKUP_INSPECTION_TIME;
var DELIVERY_INSPECTION_SEC = DELIVERY_DWELL_TIME + DELIVERY_INSPECTION_TIME;
var RETURN_RIDE_WAIT_SEC = 300;
function driveSpeed(distanceMiles, driveSeconds) {
  if (distanceMiles <= 0 || driveSeconds <= 0) return { mph: null, minPerMile: null };
  const mph = Math.round(distanceMiles / (driveSeconds / 3600) * 10) / 10;
  if (mph <= 0) return { mph: null, minPerMile: null };
  return { mph, minPerMile: Math.round(60 / mph * 100) / 100 };
}
function toRoutePreference(pref) {
  if (!pref) return void 0;
  return pref.toUpperCase();
}
function tollCentsToDollars(amountCents) {
  if (amountCents === void 0 || amountCents === null) return null;
  return Math.round(amountCents) / 100;
}
function metersToMiles(meters) {
  return Math.round(meters * METERS_TO_MILES * 100) / 100;
}
async function resolveRoutingOptions(params) {
  const { customerId, explicitOptions = {}, useConfigHierarchy, executor, logger } = params;
  let avoidTolls = false;
  let avoidTollsSource = "hopdrive";
  let avoidHighways = false;
  let avoidHighwaysSource = "hopdrive";
  let avoidFerries = false;
  let avoidFerriesSource = "hopdrive";
  let routePreference = "fastest";
  let routePreferenceSource = "hopdrive";
  if (useConfigHierarchy) {
    try {
      const config = await getCustomerConfig({
        customerId,
        executor
      });
      const routing = config.routing;
      if (routing) {
        if (routing.avoid_tolls !== void 0) {
          avoidTolls = routing.avoid_tolls;
          avoidTollsSource = "customer";
        }
        if (routing.avoid_highways !== void 0) {
          avoidHighways = routing.avoid_highways;
          avoidHighwaysSource = "customer";
        }
        if (routing.avoid_ferries !== void 0) {
          avoidFerries = routing.avoid_ferries;
          avoidFerriesSource = "customer";
        }
        if (routing.route_preference !== void 0) {
          routePreference = routing.route_preference;
          routePreferenceSource = "customer";
        }
      }
    } catch {
      logger.warn("enrichLane.configFetchFailed", { customerId, usingDefaults: true });
    }
  }
  if (explicitOptions.avoidTolls !== void 0) {
    avoidTolls = explicitOptions.avoidTolls;
    avoidTollsSource = "move";
  }
  if (explicitOptions.avoidHighways !== void 0) {
    avoidHighways = explicitOptions.avoidHighways;
    avoidHighwaysSource = "move";
  }
  if (explicitOptions.avoidFerries !== void 0) {
    avoidFerries = explicitOptions.avoidFerries;
    avoidFerriesSource = "move";
  }
  if (explicitOptions.routePreference !== void 0) {
    routePreference = explicitOptions.routePreference;
    routePreferenceSource = "move";
  }
  return {
    avoidTolls,
    avoidTollsSource,
    avoidHighways,
    avoidHighwaysSource,
    avoidFerries,
    avoidFerriesSource,
    routePreference,
    routePreferenceSource
  };
}
async function enrichLane(input) {
  const {
    laneId,
    routingOptions,
    geoProviders,
    useConfigHierarchy = false,
    logger
  } = input;
  const executor = resolveExecutor6(input.executor);
  const providers = geoProviders ?? getDefaultGeoProviders();
  const log = scopeLogger(logger ?? getLogger11(), {
    operationId: generateOperationId(),
    operation: "enrichLane",
    laneId,
    ...input.correlationId && { correlationId: input.correlationId }
  });
  const startedAt = Date.now();
  log.info("enrichLane.start", { laneId, useConfigHierarchy });
  const lane = await getLane({
    id: laneId,
    includeLocations: true,
    required: false,
    executor
  });
  if (!lane) {
    throw new LaneNotFoundError({ id: laneId });
  }
  const takeover = isDeadRun(lane);
  if (lane.status === "ready" || lane.status === "enriching" && !takeover) {
    log.debug("enrichLane.idempotentSkip", { laneId, status: lane.status });
    if (!lane.description?.trim()) {
      const description = `${locationDisplayName(lane.pickup)} to ${locationDisplayName(lane.delivery)}`;
      await updateLaneDescription2({ laneId, description, executor, ...logger && { logger } }).catch((error) => {
        log.warn("enrichLane.descriptionBackfillFailed", { laneId, error: String(error) });
      });
    }
    return buildResultFromExistingRow(lane);
  }
  const oldEnrichmentState = {
    name: lane.status,
    distance_miles: lane.distance_miles,
    duration_seconds: lane.duration_sec,
    tolls: lane.tolls,
    enrichment_source: lane.enrichment_source ?? null
  };
  const readStatus = lane.status ?? null;
  try {
    const { pickup, delivery } = lane;
    if (!pickup) {
      throw new LaneError(`Lane ${laneId} has no readable pickup location.`);
    }
    if (!delivery) {
      throw new LaneError(`Lane ${laneId} has no readable delivery location.`);
    }
    if (!pickup.latitude || !pickup.longitude) {
      throw new LaneError(
        `Pickup location ${pickup.id} is missing coordinates. Geocode the location first.`
      );
    }
    if (!delivery.latitude || !delivery.longitude) {
      throw new LaneError(
        `Delivery location ${delivery.id} is missing coordinates. Geocode the location first.`
      );
    }
    const resolvedOptions = await resolveRoutingOptions({
      customerId: lane.customer_id,
      explicitOptions: routingOptions,
      useConfigHierarchy,
      executor,
      logger: logger ?? getLogger11()
    });
    const claim = await updateLaneStatus2({
      laneId,
      status: "enriching",
      from: [readStatus],
      ...takeover && { unchangedAt: lane.updatedat },
      executor,
      ...logger && { logger }
    });
    if (claim.affectedRows === 0) {
      const current = await getLane({ id: laneId, includeLocations: true, required: false, executor });
      const currentStatus = current?.status ?? null;
      log.info("enrichLane.claimLost", { laneId, readStatus, currentStatus });
      const settled = currentStatus === "ready" || currentStatus === "failed";
      return buildResultFromExistingRow(
        settled && current ? current : { ...current ?? lane, status: "enriching" }
      );
    }
    const routeRequest = {
      origin: {
        latitude: pickup.latitude,
        longitude: pickup.longitude
      },
      destination: {
        latitude: delivery.latitude,
        longitude: delivery.longitude
      },
      travelMode: "DRIVE",
      routePreference: toRoutePreference(resolvedOptions.routePreference),
      avoidTolls: resolvedOptions.avoidTolls,
      avoidHighways: resolvedOptions.avoidHighways,
      avoidFerries: resolvedOptions.avoidFerries,
      includePolyline: true,
      includeTolls: !resolvedOptions.avoidTolls,
      // Only compute tolls if not avoiding
      ...logger && { logger }
    };
    const routeResult = await providers.routing.computeRoute(routeRequest);
    const distanceMiles = metersToMiles(routeResult.distanceMeters);
    const durationSeconds = routeResult.durationSeconds + PICKUP_INSPECTION_SEC + DELIVERY_INSPECTION_SEC;
    const speed = driveSpeed(distanceMiles, routeResult.durationSeconds);
    const pickupInspectionSec = lane.pickup_inspection_sec ?? PICKUP_INSPECTION_SEC;
    const deliveryInspectionSec = lane.delivery_inspection_sec ?? DELIVERY_INSPECTION_SEC;
    const returnRideWaitSec = lane.return_ride_wait_sec ?? RETURN_RIDE_WAIT_SEC;
    const tolls = tollCentsToDollars(routeResult.tolls?.amountCents);
    const polyline = routeResult.polyline ?? null;
    log.info("enrichLane.routeComputed", {
      laneId,
      distanceMiles,
      durationSeconds,
      tolls,
      source: routeResult.source
    });
    const routeMetadata = {
      source: routeResult.source,
      computedAt: routeResult.computedAt,
      routeSummary: routeResult.routeSummary,
      warnings: routeResult.warnings,
      staticDurationSeconds: routeResult.staticDurationSeconds,
      hasTrafficData: routeResult.durationSeconds !== routeResult.staticDurationSeconds
    };
    const creationCriteria = {
      avoid_tolls: resolvedOptions.avoidTolls,
      avoid_tolls_source: resolvedOptions.avoidTollsSource,
      route_preference: resolvedOptions.routePreference,
      route_preference_source: resolvedOptions.routePreferenceSource,
      api_version: "v1",
      created_at: (/* @__PURE__ */ new Date()).toISOString()
    };
    const description = `${locationDisplayName(pickup)} to ${locationDisplayName(delivery)}`;
    const updateResult = await updateLaneEnrichment2({
      laneId,
      description,
      distanceMiles,
      durationSeconds,
      averageDriveSpeedMph: speed.mph,
      averageDriveSpeedMinPerMile: speed.minPerMile,
      pickupInspectionSec,
      deliveryInspectionSec,
      returnRideWaitSec,
      tolls,
      polyline,
      routeMetadata,
      enrichmentSource: routeResult.source,
      creationCriteria,
      status: "ready",
      executor
    });
    log.info("enrichLane.complete", { laneId, status: "ready", elapsedMs: elapsed(startedAt) });
    logEvent4("lane.enrichment.completed", {
      user: "system",
      role: "system",
      lane_id: laneId,
      customer_id: lane.customer_id,
      metadata: {
        old: oldEnrichmentState,
        new: {
          name: "ready",
          distance_miles: distanceMiles,
          duration_seconds: durationSeconds,
          tolls,
          enrichment_source: routeResult.source
        }
      },
      fireAndForget: true
    });
    return {
      laneId,
      distanceMiles,
      durationSeconds,
      tolls,
      polyline,
      routeMetadata,
      enrichmentSource: routeResult.source,
      creationCriteria,
      status: "ready",
      enrichedAt: updateResult.updatedat,
      computed: true
    };
  } catch (error) {
    log.error("enrichLane.failed", error, { laneId, elapsedMs: elapsed(startedAt) });
    logEvent4("lane.enrichment.failed", {
      user: "system",
      role: "system",
      lane_id: laneId,
      customer_id: lane.customer_id,
      metadata: {
        failure: {
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : void 0
        }
      },
      fireAndForget: true
    });
    await updateLaneStatus2({
      laneId,
      status: "failed",
      from: [readStatus, "enriching"],
      executor,
      ...logger && { logger }
    }).catch(() => {
      log.warn("enrichLane.statusUpdateFailed", { laneId });
    });
    throw error;
  }
}
function isDeadRun(lane) {
  return lane.status === "enriching" && laneNeedsRerun(lane, laneUnchangedForMs(lane.updatedat)) === "stuck-enriching";
}
function buildResultFromExistingRow(lane) {
  const storedMetadata = lane.route_metadata ?? {};
  return {
    laneId: lane.id,
    distanceMiles: lane.distance_miles ?? 0,
    durationSeconds: lane.duration_sec ?? 0,
    tolls: lane.tolls,
    polyline: lane.route_polyline ?? null,
    routeMetadata: {
      source: lane.enrichment_source ?? storedMetadata.source ?? "unknown",
      computedAt: lane.enriched_at ?? storedMetadata.computedAt ?? (/* @__PURE__ */ new Date()).toISOString(),
      routeSummary: storedMetadata.routeSummary,
      warnings: storedMetadata.warnings,
      staticDurationSeconds: storedMetadata.staticDurationSeconds,
      hasTrafficData: storedMetadata.hasTrafficData
    },
    enrichmentSource: lane.enrichment_source ?? "unknown",
    creationCriteria: lane.creation_criteria ?? {
      created_at: lane.createdat
    },
    status: lane.status,
    enrichedAt: lane.enriched_at ?? lane.updatedat,
    computed: false
  };
}

// src/features/resolve-lane/resolveLaneWithRouting.ts
async function resolveLaneWithRouting(params) {
  const {
    customerId,
    originLocationId,
    destinationLocationId,
    routingOptions,
    useConfigHierarchy = false,
    executor,
    correlationId,
    waitForReadyMs = 3e4,
    pollIntervalMs = 250,
    logger
  } = params;
  const log = scopeLogger(logger ?? getLogger12(), {
    operationId: generateOperationId(),
    operation: "resolveLaneWithRouting",
    customerId,
    ...correlationId && { correlationId }
  });
  const startedAt = Date.now();
  const { lane, inverse, created } = await resolveLane({
    customerId,
    originLocationId,
    destinationLocationId,
    executor,
    correlationId,
    ...logger && { logger }
  });
  log.debug("resolveLaneWithRouting.resolved", {
    laneId: lane.id,
    inverseId: inverse?.id ?? null,
    created,
    laneStatus: lane.status,
    inverseStatus: inverse?.status ?? null
  });
  const enrichSettings = { routingOptions, useConfigHierarchy, executor, correlationId, logger };
  const [forwardReady, inverseReady] = await Promise.all([
    ensureReady({ laneId: lane.id, waitForReadyMs, pollIntervalMs, executor, ...enrichSettings }),
    inverse ? ensureReady({ laneId: inverse.id, waitForReadyMs, pollIntervalMs, executor, ...enrichSettings }) : Promise.resolve(null)
  ]);
  log.info("resolveLaneWithRouting.complete", {
    laneId: lane.id,
    inverseId: inverse?.id ?? null,
    created,
    elapsedMs: elapsed(startedAt)
  });
  return {
    lane: forwardReady,
    inverse: inverseReady,
    created
  };
}
async function ensureReady(args) {
  const { laneId, waitForReadyMs, executor } = args;
  const status = await enrichOrFail(args);
  if (status === "enriching") {
    const lane = await waitForOutcome(args, Date.now() + waitForReadyMs);
    if (lane) return lane;
    throw new LaneEnrichmentFailedError(
      laneId,
      new Error(`enrichment did not complete within ${waitForReadyMs}ms`)
    );
  }
  return readEnrichedLane(laneId, executor);
}
async function readEnrichedLane(laneId, executor) {
  const lane = await getLane({ id: laneId, includeLocations: false, required: false, executor });
  if (!lane) {
    throw new LaneEnrichmentFailedError(laneId, new Error("lane disappeared after enrichment"));
  }
  return lane;
}
async function enrichOrFail(args) {
  const { laneId, routingOptions, useConfigHierarchy, executor, correlationId, logger } = args;
  let result;
  try {
    result = await enrichLane({
      laneId,
      routingOptions,
      useConfigHierarchy,
      executor,
      correlationId,
      ...logger && { logger }
    });
  } catch (error) {
    throw new LaneEnrichmentFailedError(laneId, error instanceof Error ? error : void 0);
  }
  if (result.status === "failed") {
    throw new LaneEnrichmentFailedError(laneId);
  }
  return result.status;
}
async function waitForOutcome(args, deadline) {
  const { laneId, pollIntervalMs, executor } = args;
  while (Date.now() < deadline) {
    await sleep(pollIntervalMs);
    const lane = await getLane({ id: laneId, includeLocations: false, required: false, executor });
    if (!lane) {
      throw new LaneEnrichmentFailedError(laneId, new Error("lane disappeared while polling"));
    }
    if (lane.status === "ready") return lane;
    if (lane.status === "failed") throw new LaneEnrichmentFailedError(laneId);
    if (laneNeedsRerun(lane, laneUnchangedForMs(lane.updatedat)) && await enrichOrFail(args) === "ready") {
      return readEnrichedLane(laneId, executor);
    }
  }
  return null;
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// src/features/location-update/updateLocationCoordinates.ts
import { resolveExecutor as resolveExecutor7 } from "@hopdrive/sdk-core";
import { logEvent as logEvent5 } from "@hopdrive/sdk-events";
async function updateLocationCoordinates2(params) {
  const executor = resolveExecutor7(params.executor);
  const oldLocation = await getLocation({ id: params.locationId, required: true, executor });
  const coordinatesSource = params.coordinatesSource ?? "user_adjusted";
  const result = await updateLocationCoordinates(
    {
      id: params.locationId,
      latitude: params.latitude,
      longitude: params.longitude,
      coordinates_source: coordinatesSource
    },
    { executor }
  );
  const updated = result;
  logEvent5("location.coordinates.updated", {
    user: params.user ?? "system",
    role: params.role ?? "system",
    location_id: params.locationId,
    customer_id: oldLocation.customer_id ?? void 0,
    metadata: {
      old: {
        name: oldLocation.coordinates_source ?? "geocoded",
        latitude: oldLocation.latitude,
        longitude: oldLocation.longitude
      },
      new: {
        name: coordinatesSource,
        latitude: params.latitude,
        longitude: params.longitude
      }
    },
    fireAndForget: true
  });
  return updated;
}

// src/features/location-update/updateLocationTimezone.ts
import { resolveExecutor as resolveExecutor8 } from "@hopdrive/sdk-core";
import { logEvent as logEvent6 } from "@hopdrive/sdk-events";
async function updateLocationTimezone2(params) {
  const executor = resolveExecutor8(params.executor);
  const result = await updateLocationTimezone(
    {
      id: params.locationId,
      timezone: params.timezone
    },
    { executor }
  );
  const updated = result;
  logEvent6("location.timezone.updated", {
    user: params.user ?? "system",
    role: params.role ?? "system",
    location_id: params.locationId,
    metadata: {
      timezone: params.timezone
    },
    fireAndForget: true
  });
  return updated;
}

// src/features/enrichment/requestLaneEnrichment.ts
import { resolveExecutor as resolveExecutor9, getLogger as getLogger13 } from "@hopdrive/sdk-core";
async function requestLaneEnrichment(params) {
  const { laneId, user, role, customerId, metadata } = params;
  const executor = resolveExecutor9(params.executor);
  const log = scopeLogger(params.logger ?? getLogger13(), {
    operationId: generateOperationId(),
    operation: "requestLaneEnrichment",
    laneId,
    ...params.correlationId && { correlationId: params.correlationId }
  });
  const startedAt = Date.now();
  log.info("requestLaneEnrichment.start", { laneId, user, role });
  const lane = await getLane({ id: laneId, required: false, executor });
  const snapshot = lane ? laneEnrichmentSnapshot(lane) : null;
  await updateLaneStatus2({ laneId, status: "pending", executor, ...params.logger && { logger: params.logger } });
  const resolvedCustomerId = customerId ?? lane?.customer_id;
  const event = await logLaneEnrichmentRequested({
    laneId,
    user,
    role,
    customerId: resolvedCustomerId,
    snapshot,
    metadata,
    executor
  });
  log.info("requestLaneEnrichment.complete", {
    laneId,
    eventId: event?.id ?? -1,
    elapsedMs: elapsed(startedAt)
  });
  return {
    laneId,
    status: "pending",
    eventId: event?.id ?? -1
  };
}

// src/features/suggestions/suggestAddresses.ts
import { getLogger as getLogger14 } from "@hopdrive/sdk-core";
async function suggestAddresses(params) {
  const { suggestion } = getDefaultGeoProviders();
  const apiStart = Date.now();
  const result = await suggestion.suggest({
    input: params.input,
    sessionToken: params.sessionToken,
    regionBias: params.regionBias,
    locationBias: params.locationBias ? {
      center: {
        latitude: params.locationBias.latitude,
        longitude: params.locationBias.longitude
      },
      radiusMeters: params.locationBias.radiusMeters
    } : void 0,
    includedRegionCodes: params.includedRegionCodes,
    includedTypes: params.includedTypes,
    language: params.language,
    ...params.logger && { logger: params.logger }
  });
  const log = params.logger ?? getLogger14();
  log.debug("suggestAddresses.complete", {
    input: params.input,
    predictionCount: result.predictions.length,
    apiMs: Date.now() - apiStart
  });
  return result;
}

// src/features/suggestions/sessionToken.ts
function createSessionToken() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
export {
  AmbiguousLocationError,
  DEFAULT_MATCHING_CONFIG,
  DuplicateLocationError,
  GeoApiError,
  GeoError,
  GeocodingError as GeoGeocodingError,
  GeoRateLimitError,
  GeocodingError2 as GeocodingError,
  GoogleClient,
  GoogleGeocodingProvider,
  GoogleRoutesProvider,
  GoogleSuggestionProvider,
  GoogleTimezoneProvider,
  LANE_STUCK_AFTER_MS,
  LaneEnrichmentFailedError,
  LaneError,
  LaneNotFoundError,
  LaneNotReadyError,
  LaneValidationError,
  LocalTimezoneProvider,
  LocationError,
  LocationNotFoundError,
  LocationValidationError,
  RoutingError,
  SuggestionError,
  createDefaultProviders,
  createFetchExecutor,
  createGoogleGeocoder,
  createGoogleProviders,
  createGoogleRouter,
  createGoogleSuggester,
  createLaneShell,
  createLocation2 as createLocation,
  createSessionToken,
  enrichLane,
  findLocationByAlias,
  findLocationByCustomerRef,
  findLocationByPlaceId,
  findLocationsNearby2 as findLocationsNearby,
  generatePlusCode,
  geocodeAddress,
  getDefaultGeoProviders,
  getGoogleApiKey,
  getLane,
  getLocation,
  getLogger,
  init,
  isInitialized,
  laneNeedsRerun,
  normalizeForComparison,
  normalizeInputForAlias,
  requestLaneEnrichment,
  rerunLane,
  resetDefaultExecutor,
  resetLanesConfig,
  resolveExecutor,
  resolveLane,
  resolveLaneWithRouting,
  resolveLocation,
  resolveTimezone,
  scoreCandidates,
  suggestAddresses,
  updateLaneEnrichment2 as updateLaneEnrichment,
  updateLaneStatus2 as updateLaneStatus,
  updateLocationCoordinates2 as updateLocationCoordinates,
  updateLocationTimezone2 as updateLocationTimezone
};
//# sourceMappingURL=index.mjs.map